import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UploadCloud, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";
import { parseExcel, generateExcelTemplate } from "@/utils/excelParser";

interface BulkImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: any[]) => Promise<void>;
  type: "candidates" | "propositions";
}

const TEMPLATES = {
  candidates: ["firstName", "lastName", "parti", "region", "province", "listType", "genre", "dateNaissance", "categorieSociopro", "bio"],
  propositions: ["theme", "domain", "parti", "textFr", "textAr", "keywords"]
};

export function BulkImportDialog({ isOpen, onClose, onImport, type }: BulkImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setErrors([]);
    setParsedData([]);

    try {
      const expectedColumns = TEMPLATES[type];
      const result = await parseExcel<any>(selectedFile, expectedColumns);
      
      if (result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        setParsedData(result.data);
      }
    } catch (err) {
      setErrors(["Erreur lors de la lecture du fichier Excel."]);
    }
  };

  const handleDownloadTemplate = () => {
    const filename = type === "candidates" ? "Modele_Candidats.xlsx" : "Modele_Propositions.xlsx";
    generateExcelTemplate(TEMPLATES[type], filename, type);
  };

  const handleConfirm = async () => {
    if (parsedData.length === 0) return;
    
    setIsUploading(true);
    try {
      await onImport(parsedData);
      onClose();
      // Reset state on success
      setTimeout(() => {
        setFile(null);
        setParsedData([]);
        setErrors([]);
      }, 500);
    } catch (e) {
      setErrors(["Erreur lors de l'enregistrement en base de données."]);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isUploading && !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Importation par Lot (Excel)</DialogTitle>
          <DialogDescription>
            Ajoutez plusieurs {type === 'candidates' ? 'candidats' : 'propositions'} en téléversant un fichier Excel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex justify-between items-center bg-blue-50 p-3 rounded-lg border border-blue-100">
            <span className="text-sm text-blue-800 font-medium">1. Téléchargez le modèle</span>
            <Button size="sm" variant="outline" className="h-8 bg-white border-blue-200 text-blue-700" onClick={handleDownloadTemplate}>
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Modèle
            </Button>
          </div>

          <div 
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${file ? 'border-emerald-300 bg-emerald-50' : 'border-border hover:bg-muted/50'}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input 
              type="file" 
              accept=".xlsx,.xls,.csv" 
              className="hidden" 
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            
            {file ? (
              <div className="flex flex-col items-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-2" />
                <p className="font-semibold text-emerald-800">{file.name}</p>
                <p className="text-xs text-emerald-600 mt-1">{parsedData.length} lignes valides trouvées</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <UploadCloud className="w-10 h-10 text-muted-foreground mb-2" />
                <p className="font-semibold text-foreground">2. Cliquez ici pour uploader</p>
                <p className="text-xs text-muted-foreground mt-1">Formats acceptés : .xlsx, .csv</p>
              </div>
            )}
          </div>

          {errors.length > 0 && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg border border-red-200 text-sm space-y-1">
              <div className="flex items-center font-bold mb-1"><AlertTriangle className="w-4 h-4 mr-2"/> Erreurs trouvées :</div>
              {errors.map((e, i) => <p key={i}>• {e}</p>)}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUploading}>Annuler</Button>
          <Button 
            disabled={parsedData.length === 0 || errors.length > 0 || isUploading} 
            onClick={handleConfirm}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isUploading ? "Importation en cours..." : `Importer ${parsedData.length} éléments`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
